import { useState, useEffect, useCallback } from 'react';
import './ScanTab.css';
import { supabase } from './supabase';
import { useEffectiveUser } from './ImpersonationContext';

// Mêmes constantes que scan-bot.js — pour que l'estimation affichée corresponde
// exactement à ce que le backend calculera réellement au prochain scan.
const CREDITS_PER_KEY_PER_DAY = Math.floor(1_000_000 / 30);
const FIXED_SCAN_COST_CREDITS = 1800;
const SCANS_PER_DAY = 12;

function computeWalletsPerScan(numActiveKeys) {
  if (numActiveKeys <= 0) return 0;
  const budgetDaily = numActiveKeys * CREDITS_PER_KEY_PER_DAY;
  const raw = (budgetDaily - FIXED_SCAN_COST_CREDITS * SCANS_PER_DAY) / 300 / SCANS_PER_DAY;
  return Math.max(1, Math.floor(raw));
}

function timeAgo(isoTs) {
  if (!isoTs) return '—';
  const diff = Math.floor((Date.now() - new Date(isoTs).getTime()) / 1000);
  if (diff < 60)    return 'il y a quelques secondes';
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)} jours`;
}

function timeUntil(isoTs) {
  if (!isoTs) return '—';
  const diff = Math.floor((new Date(isoTs).getTime() - Date.now()) / 1000);
  if (diff <= 0) return 'imminent';
  if (diff < 3600) return `dans ${Math.floor(diff / 60)} min`;
  return `dans ${Math.floor(diff / 3600)}h${Math.round((diff % 3600) / 60)}`;
}

const truncAddr = a => `${a.slice(0, 6)}…${a.slice(-4)}`;
const truncKey  = k => k.length > 10 ? `${k.slice(0, 6)}…${k.slice(-4)}` : k;

function scoreClass(score) {
  if (score == null) return 'sc-score--low';
  if (score >= 60) return 'sc-score--high';
  if (score >= 40) return 'sc-score--mid';
  return 'sc-score--low';
}

export default function ScanTab() {
  const { effectiveUserId } = useEffectiveUser();

  const [loading,      setLoading]      = useState(true);
  const [apiKeys,      setApiKeys]      = useState([]);
  const [settings,     setSettings]     = useState(null);
  const [wallets,      setWallets]      = useState([]);
  const [showAddKey,   setShowAddKey]   = useState(false);
  const [newKeyLabel,  setNewKeyLabel]  = useState('');
  const [newKeyValue,  setNewKeyValue]  = useState('');
  const [savingKey,    setSavingKey]    = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery,  setSearchQuery]  = useState('');
  const [sortKey,      setSortKey]      = useState('score');
  const [sortDir,      setSortDir]      = useState('desc');
  const [copiedId,     setCopiedId]     = useState(null);

  const loadAll = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = effectiveUserId || session?.user?.id;
    if (!userId) { setLoading(false); return; }

    const [{ data: keys }, { data: st }, { data: ws }] = await Promise.all([
      supabase.from('scan_api_keys').select('*').eq('user_id', userId).order('created_at', { ascending: true }),
      supabase.from('scan_settings').select('*').eq('user_id', userId).maybeSingle(),
      supabase.from('scan_wallets').select('*').eq('user_id', userId).order('score', { ascending: false, nullsFirst: false }),
    ]);

    setApiKeys(keys || []);
    setSettings(st || null);
    setWallets(ws || []);
    setLoading(false);
  }, [effectiveUserId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleAddKey = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = effectiveUserId || session?.user?.id;
    if (!userId || !newKeyValue.trim()) return;
    setSavingKey(true);
    const { error } = await supabase.from('scan_api_keys').insert({
      user_id: userId,
      label: newKeyLabel.trim() || `Clé ${apiKeys.length + 1}`,
      api_key: newKeyValue.trim(),
    });
    setSavingKey(false);
    if (!error) {
      setNewKeyLabel('');
      setNewKeyValue('');
      setShowAddKey(false);
      loadAll();
    }
  };

  const handleDeleteKey = async (id) => {
    await supabase.from('scan_api_keys').delete().eq('id', id);
    loadAll();
  };

  const handleCopy = (w) => {
    navigator.clipboard.writeText(w.wallet_address);
    setCopiedId(w.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  if (loading) {
    return <div className="sc-root"><div className="sc-loading">Chargement du Scan…</div></div>;
  }

  const activeKeys    = apiKeys.filter(k => k.active);
  const walletsPerScan = computeWalletsPerScan(activeKeys.length);
  const walletsPerDay  = walletsPerScan * SCANS_PER_DAY;

  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const addedToday = wallets.filter(w => w.discovered_at && new Date(w.discovered_at) >= todayStart).length;

  const cutoff24h = Date.now() - 24 * 3600 * 1000;
  const score60Count = wallets.filter(w => (w.score ?? 0) > 60).length;
  const score40Count = wallets.filter(w => (w.score ?? 0) > 40).length;
  const actifs24hCount = wallets.filter(w => w.last_active_at && new Date(w.last_active_at).getTime() >= cutoff24h).length;

  const visibleWallets = (() => {
    let r = wallets;
    if (filterStatus === 'score60')   r = r.filter(w => (w.score ?? 0) > 60);
    if (filterStatus === 'score40')   r = r.filter(w => (w.score ?? 0) > 40);
    if (filterStatus === 'actifs24h') r = r.filter(w => w.last_active_at && new Date(w.last_active_at).getTime() >= cutoff24h);
    if (searchQuery.trim()) r = r.filter(w => w.wallet_address.toLowerCase().includes(searchQuery.toLowerCase().trim()));
    return r;
  })();

  const sortedWallets = [...visibleWallets].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string') return sortDir === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv);
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  return (
    <div className="sc-root">

      {/* ── Clés API ── */}
      <div className="sc-section">
        <div className="sc-section-title">Clés API</div>
        {apiKeys.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-4)' }}>Aucune clé ajoutée pour l'instant.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {apiKeys.map(k => (
              <div key={k.id} className="sc-key-row">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="sc-key-label">{k.label}</span>
                  <span className="sc-key-value">{truncKey(k.api_key)}</span>
                  <span className={`sc-key-status sc-key-status--${k.active ? 'active' : 'exhausted'}`}>
                    {k.active ? 'active' : 'épuisée'}
                  </span>
                </div>
                <button className="sc-btn sc-btn--danger" onClick={() => handleDeleteKey(k.id)}>Supprimer</button>
              </div>
            ))}
          </div>
        )}

        {showAddKey ? (
          <div className="sc-key-form">
            <input
              className="sc-input"
              placeholder="Label (ex: Clé 1)"
              value={newKeyLabel}
              onChange={e => setNewKeyLabel(e.target.value)}
              style={{ width: 160 }}
            />
            <input
              className="sc-input"
              placeholder="Clé API Helius"
              value={newKeyValue}
              onChange={e => setNewKeyValue(e.target.value)}
              style={{ width: 280 }}
            />
            <button className="sc-btn sc-btn--accent" onClick={handleAddKey} disabled={savingKey || !newKeyValue.trim()}>
              {savingKey ? 'Sauvegarde…' : 'Sauvegarder les clés'}
            </button>
            <button className="sc-btn sc-btn--ghost" onClick={() => setShowAddKey(false)}>Annuler</button>
          </div>
        ) : (
          <button className="sc-btn sc-btn--accent" style={{ alignSelf: 'flex-start' }} onClick={() => setShowAddKey(true)}>
            + Ajouter une clé
          </button>
        )}

        <div className="sc-budget-line">
          {activeKeys.length} clé{activeKeys.length !== 1 ? 's' : ''} × 1M = <b>{activeKeys.length}M</b> crédits
          {' | ~'}<b>{walletsPerScan}</b> wallets/scan
          {' | ~'}<b>{walletsPerDay}</b> wallets/jour
        </div>
      </div>

      {/* ── Statut ── */}
      <div className="sc-section">
        <div className="sc-section-title">Statut</div>
        <div className="sc-stats-row">
          <div className="sc-stat-card">
            <div className="sc-stat-label">Dernier scan</div>
            <div className="sc-stat-value" style={{ fontSize: 13 }}>{timeAgo(settings?.last_scan_at)}</div>
          </div>
          <div className="sc-stat-card">
            <div className="sc-stat-label">Prochain scan</div>
            <div className="sc-stat-value" style={{ fontSize: 13 }}>{timeUntil(settings?.next_scan_at)}</div>
          </div>
          <div className="sc-stat-card">
            <div className="sc-stat-label">Total wallets découverts</div>
            <div className="sc-stat-value">{wallets.length}</div>
          </div>
          <div className="sc-stat-card">
            <div className="sc-stat-label">Ajoutés aujourd'hui</div>
            <div className="sc-stat-value">{addedToday}</div>
          </div>
        </div>
      </div>

      {/* ── Wallets découverts ── */}
      <div className="sc-filter-bar">
        {[
          { key: 'all',        label: 'Tous',         count: wallets.length },
          { key: 'score60',    label: 'Score > 60',   count: score60Count },
          { key: 'score40',    label: 'Score > 40',   count: score40Count },
          { key: 'actifs24h',  label: 'Actifs < 24h', count: actifs24hCount },
        ].map(({ key, label, count }) => (
          <button
            key={key}
            className={`sc-filter-btn${filterStatus === key ? ' sc-filter-btn--active' : ''}`}
            onClick={() => setFilterStatus(key)}
          >
            {label}<span className="sc-filter-count">{count}</span>
          </button>
        ))}
        <input
          type="text"
          placeholder="Rechercher adresse…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="sc-input"
          style={{ marginLeft: 'auto', width: 160 }}
        />
      </div>

      {wallets.length === 0 ? (
        <div className="sc-empty">Aucun wallet découvert pour l'instant — le scan tourne toutes les 2h en arrière-plan.</div>
      ) : sortedWallets.length === 0 ? (
        <div className="sc-empty">Aucun wallet dans cette catégorie.</div>
      ) : (
        <div className="sc-table-wrap">
          <table className="sc-table">
            <thead>
              <tr>
                <th>Adresse</th>
                <th className={`sc-th-sort${sortKey === 'score' ? ' sc-th-sort--active' : ''}`} onClick={() => handleSort('score')}>
                  Score<span className="sc-sort-icon">{sortKey === 'score' ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}</span>
                </th>
                <th className={`sc-th-sort${sortKey === 'win_rate' ? ' sc-th-sort--active' : ''}`} onClick={() => handleSort('win_rate')}>
                  Win Rate<span className="sc-sort-icon">{sortKey === 'win_rate' ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}</span>
                </th>
                <th className={`sc-th-sort${sortKey === 'roi_moyen' ? ' sc-th-sort--active' : ''}`} onClick={() => handleSort('roi_moyen')}>
                  ROI moyen<span className="sc-sort-icon">{sortKey === 'roi_moyen' ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}</span>
                </th>
                <th className={`sc-th-sort${sortKey === 'best_trade' ? ' sc-th-sort--active' : ''}`} onClick={() => handleSort('best_trade')}>
                  Best trade<span className="sc-sort-icon">{sortKey === 'best_trade' ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}</span>
                </th>
                <th className={`sc-th-sort${sortKey === 'nb_trades' ? ' sc-th-sort--active' : ''}`} onClick={() => handleSort('nb_trades')}>
                  Nb trades<span className="sc-sort-icon">{sortKey === 'nb_trades' ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}</span>
                </th>
                <th className={`sc-th-sort${sortKey === 'last_active_at' ? ' sc-th-sort--active' : ''}`} onClick={() => handleSort('last_active_at')}>
                  Dernière activité<span className="sc-sort-icon">{sortKey === 'last_active_at' ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}</span>
                </th>
                <th className={`sc-th-sort${sortKey === 'discovered_at' ? ' sc-th-sort--active' : ''}`} onClick={() => handleSort('discovered_at')}>
                  Découvert le<span className="sc-sort-icon">{sortKey === 'discovered_at' ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedWallets.map(w => (
                <tr key={w.id} className="sc-row">
                  <td className="sc-cell-addr">
                    <button className="sc-addr-copy" onClick={() => handleCopy(w)} title={copiedId === w.id ? 'Copié !' : "Copier l'adresse"}>
                      {copiedId === w.id ? '✓ Copié' : truncAddr(w.wallet_address)}
                    </button>
                    <a href={`https://solscan.io/account/${w.wallet_address}`} target="_blank" rel="noopener noreferrer" className="sc-addr-ext" title="Ouvrir dans Solscan">↗</a>
                  </td>
                  <td><span className={`sc-score ${scoreClass(w.score)}`}>{w.score ?? '—'}</span></td>
                  <td className="sc-cell-num">{w.win_rate != null ? `${Math.round(w.win_rate)}%` : '—'}</td>
                  <td className="sc-cell-num">{w.roi_moyen != null ? `${w.roi_moyen >= 0 ? '+' : ''}${w.roi_moyen.toFixed(1)}%` : '—'}</td>
                  <td className="sc-cell-num">{w.best_trade != null ? `+${Math.round(w.best_trade)}%` : '—'}</td>
                  <td className="sc-cell-num">{w.nb_trades ?? '—'}</td>
                  <td style={{ fontSize: 11, color: 'var(--text-3)' }}>{timeAgo(w.last_active_at)}</td>
                  <td style={{ fontSize: 11, color: 'var(--text-4)' }}>{timeAgo(w.discovered_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
